/* SUMMARY
  * toTimestamp: read a date coming either as an ISO string, a Date or an epoch number
  * getWorkorderTimeIssue: which consistency rule the real intervention dates break
  * getWorkorderTimeError: the translated message for the rule they break
*/

/**
* WorkorderTimeIssue: the rules the real start/end of an intervention must hold,
* named after their key under `form:workorder:errors`
*/
export type WorkorderTimeIssue = "dateCannotBeInFuture" | "startDateMustBeBeforeEndDate";

/**
* toTimestamp: read a date coming either as an ISO string, a Date or an epoch number
* @param value - value to read
* @return - epoch milliseconds, or null when there is no usable date
*/
const toTimestamp = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const time = new Date(value as string | number | Date).getTime();
  return isNaN(time) ? null : time;
};
/***/

/**
* getWorkorderTimeIssue: check the real start/end of an intervention against the consistency rules
* The real dates record what has already happened, so the end must follow the start and neither may
* sit in the future. A partial range stays acceptable: the fields can be hidden or not filled in yet.
* @param time - real time range to check
* @return - the rule broken, or null when the range is consistent
*/
export const getWorkorderTimeIssue = (time: unknown): WorkorderTimeIssue | null => {
  if (!time) return null;

  const range = time as { start?: unknown, end?: unknown };
  const start = toTimestamp(range.start);
  const end = toTimestamp(range.end);
  const now = Date.now();

  if ((start !== null && start > now) || (end !== null && end > now)) return "dateCannotBeInFuture";
  if (start === null || end === null) return null;
  if (start >= end) return "startDateMustBeBeforeEndDate";
  return null;
};
/***/

/**
* getWorkorderTimeError: the translated message for the rule the real dates break
* @param time - real time range to check
* @param t - translation function
* @return - the message to show, empty when the range is consistent
*/
export const getWorkorderTimeError = (time: unknown, t: (key: string) => string): string => {
  const issue = getWorkorderTimeIssue(time);
  return issue ? t(`form:workorder:errors:${issue}`) : "";
};
/***/
